import type { AssistantPromptAction, AssistantPromptPayload } from '@triedge/domain-core';
import type { AdmissionsAIContext } from './types';
import {
  type AssistantCapabilityCatalogScope,
  type AssistantCapabilityBindingValidationResult,
  validateAssistantCapabilityBindingForRoute,
} from '../../config/ai-assistant/capability-matrix';
import {
  isAssistantRouteTemplatePathname,
  routeMatchesAnyAssistantPattern,
  stripAssistantRoutePatternQualifier,
} from '../../config/ai-assistant/route-matching';

export type ExplicitPromptBinding = {
  module?: AdmissionsAIContext['module'];
  activeCapabilityId?: string;
  activeScenarioKey?: string;
  activeCapabilityMethod?: string;
  executionPathname?: string;
  originPathname?: string;
  catalogScope?: AssistantCapabilityCatalogScope;
};

export const resolveConcreteAssistantExecutionPathname = (input: {
  targetRoute?: string | null;
  routePrefixes?: string[] | null;
  currentPathname: string;
  activeRoutePathname?: string | null;
  fallbackPathname?: string | null;
}): string => {
  const currentPathname =
    toOptionalText(input.currentPathname) ??
    toOptionalText(input.fallbackPathname) ??
    '/';
  const activeRoutePathname = toOptionalText(input.activeRoutePathname);
  if (!isAssistantRouteTemplatePathname(currentPathname)) {
    return currentPathname;
  }
  if (activeRoutePathname && !isAssistantRouteTemplatePathname(activeRoutePathname)) {
    return activeRoutePathname;
  }
  const routePrefixes = input.routePrefixes ?? [];
  if (
    routePrefixes.length > 0 &&
    routeMatchesAnyAssistantPattern(currentPathname, routePrefixes) &&
    !isAssistantRouteTemplatePathname(currentPathname)
  ) {
    return currentPathname;
  }
  const candidates = [
    stripAssistantRoutePatternQualifier(input.targetRoute ?? null),
    stripAssistantRoutePatternQualifier(routePrefixes[0] ?? null),
    input.fallbackPathname,
  ];
  for (const candidate of candidates) {
    const normalized = toOptionalText(candidate);
    if (normalized && !isAssistantRouteTemplatePathname(normalized)) {
      return normalized;
    }
  }
  return currentPathname;
};

export const isStudentsFamiliesFamilyRouteSummaryPrompt = (input: {
  pathname?: string | null;
  question?: string | null;
}): boolean => {
  const pathname = toOptionalText(input.pathname) ?? '';
  if (
    !pathname.startsWith('/school/students/family/') ||
    isAssistantRouteTemplatePathname(pathname)
  ) {
    return false;
  }
  const normalizedQuestion = String(input.question ?? '').trim().toLowerCase();
  if (!normalizedQuestion) {
    return false;
  }
  if (/\b(phone|phone\s+number|home\s+phone|grade)\b/.test(normalizedQuestion)) {
    return false;
  }
  if (
    (/\bsummari[sz]e\b|\bsummary\b|\breview\b/.test(normalizedQuestion)) &&
    (
      /\bthis\s+family\b/.test(normalizedQuestion) ||
      /\bfamily\s+profile\b/.test(normalizedQuestion) ||
      /\bprofile\s+fields?\b/.test(normalizedQuestion) ||
      /\bstatus\s+signals?\b/.test(normalizedQuestion) ||
      /\bfollow[-\s]?up\s+risks?\b/.test(normalizedQuestion)
    )
  ) {
    return true;
  }
  return (
    /\bprofile\b/.test(normalizedQuestion) &&
    /\b(status|signal|follow[-\s]?up|risk|field|household|contact|schedul(?:e|ing))\b/.test(normalizedQuestion)
  );
};

const toOptionalText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
};

export const coerceExplicitPromptBinding = (value: unknown): ExplicitPromptBinding | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const module = toOptionalText(record.module) as AdmissionsAIContext['module'] | undefined;
  const activeCapabilityId = toOptionalText(record.activeCapabilityId);
  const activeScenarioKey = toOptionalText(record.activeScenarioKey);
  const activeCapabilityMethod = toOptionalText(record.activeCapabilityMethod);
  const executionPathname = toOptionalText(record.executionPathname);
  const originPathname = toOptionalText(record.originPathname);
  const catalogScopeCandidate = toOptionalText(record.catalogScope);
  const catalogScope =
    catalogScopeCandidate === 'route' ||
    catalogScopeCandidate === 'module' ||
    catalogScopeCandidate === 'staff-global'
      ? catalogScopeCandidate
      : undefined;
  if (!module && !activeCapabilityId && !activeScenarioKey && !activeCapabilityMethod && !executionPathname) {
    return undefined;
  }
  return {
    module,
    activeCapabilityId,
    activeScenarioKey,
    activeCapabilityMethod,
    executionPathname,
    originPathname,
    catalogScope,
  };
};

export const buildExplicitPromptBindingFromAssistantPrompt = (
  prompt: Pick<AssistantPromptPayload, 'promptBinding'> | null | undefined,
): ExplicitPromptBinding | undefined => coerceExplicitPromptBinding(prompt?.promptBinding ?? null);

export const buildExplicitPromptBindingFromPromptAction = (
  action: Pick<AssistantPromptAction, 'capabilityId'> | null | undefined,
  prompt: Pick<AssistantPromptPayload, 'promptBinding'> | null | undefined,
): ExplicitPromptBinding | undefined => {
  const promptBinding = buildExplicitPromptBindingFromAssistantPrompt(prompt);
  const actionCapabilityId = toOptionalText(action?.capabilityId);
  if (!promptBinding && !actionCapabilityId) {
    return undefined;
  }
  return {
    ...promptBinding,
    ...(actionCapabilityId ? { activeCapabilityId: actionCapabilityId } : {}),
  };
};

export const validateExplicitPromptBindingForRoute = (input: {
  binding: ExplicitPromptBinding | null | undefined;
  pathname?: string | null;
  persona?: string | null;
}): AssistantCapabilityBindingValidationResult => validateAssistantCapabilityBindingForRoute({
  pathname: input.binding?.executionPathname ?? input.pathname,
  persona: input.persona,
  capabilityId: input.binding?.activeCapabilityId ?? null,
  scenarioKey: input.binding?.activeScenarioKey ?? null,
  method: input.binding?.activeCapabilityMethod ?? null,
});

export const formatExplicitPromptBindingRouteMismatchMessage = (
  validation: AssistantCapabilityBindingValidationResult,
): string => {
  if (validation.reasonCode === 'capability-not-found') {
    return 'That saved prompt is no longer available. Ask from the current route.';
  }
  if (validation.targetLabel) {
    return `That prompt belongs to ${validation.targetLabel}. Open that page or ask from the current route.`;
  }
  return 'That prompt no longer matches this route. Ask from the current route.';
};
